import { apiClient } from './client';
import { API_ENDPOINTS } from './endpoints';
import { CalendarDomain } from '../@shared/interfaces/calendar.interfaces';

export interface CalendarAuthResponse {
  authUrl: string;
}

export interface CalendarConnectRequest {
  code: string;
}

export interface CalendarConnectResponse {
  success: boolean;
  message: string;
}

export interface CalendarDisconnectResponse {
  success: boolean;
  message: string;
}

/**
 * Calendar Management Service - Handles calendar integration operations
 * Provides a clean API layer between hooks and backend endpoints
 */
export class CalendarManagementService {
  private static instance: CalendarManagementService;

  private constructor() {}

  static getInstance(): CalendarManagementService {
    if (!CalendarManagementService.instance) {
      CalendarManagementService.instance = new CalendarManagementService();
    }
    return CalendarManagementService.instance;
  }

  // Get OAuth URLs
  async getGoogleAuthUrl(): Promise<string> {
    try {
      const { data } = await apiClient.get<CalendarAuthResponse>(
        API_ENDPOINTS.GOOGLE_AUTH_URL
      );
      return data.authUrl;
    } catch (error) {
      console.error('Failed to get Google OAuth URL:', error);
      throw new Error('Failed to get Google Calendar authorization URL');
    }
  }

  async getOutlookAuthUrl(state: string): Promise<string> {
    try {
      const { data } = await apiClient.get<CalendarAuthResponse>(
        API_ENDPOINTS.OUTLOOK_AUTH_URL,
        { params: { state } }
      );
      return data.authUrl;
    } catch (error) {
      console.error('Failed to get Outlook OAuth URL:', error);
      throw new Error('Failed to get Outlook Calendar authorization URL');
    }
  }

  // Connect calendars
  async connectGoogleCalendar(code: string): Promise<CalendarConnectResponse> {
    try {
      const { data } = await apiClient.post<CalendarConnectResponse>(
        API_ENDPOINTS.CONNECT_GOOGLE_CALENDAR,
        { code }
      );
      return data;
    } catch (error) {
      console.error('Failed to connect Google Calendar:', error);
      throw new Error('Failed to connect Google Calendar');
    }
  }

  async connectOutlookCalendar(code: string): Promise<CalendarConnectResponse> {
    try {
      const { data } = await apiClient.post<CalendarConnectResponse>(
        API_ENDPOINTS.CONNECT_OUTLOOK_CALENDAR,
        { code }
      );
      return data;
    } catch (error) {
      console.error('Failed to connect Outlook Calendar:', error);
      throw new Error('Failed to connect Outlook Calendar');
    }
  }

  // Unified connect method
  async connectCalendar(
    code: string,
    domain: CalendarDomain
  ): Promise<CalendarConnectResponse> {
    switch (domain) {
      case CalendarDomain.GOOGLE:
        return this.connectGoogleCalendar(code);
      case CalendarDomain.OUTLOOK:
        return this.connectOutlookCalendar(code);
      default:
        throw new Error(`Unsupported calendar domain: ${domain}`);
    }
  }

  // Disconnect calendar
  async disconnectCalendar(
    calendarId: string,
    domain: CalendarDomain
  ): Promise<CalendarDisconnectResponse> {
    try {
      const { data } = await apiClient.delete<CalendarDisconnectResponse>(
        API_ENDPOINTS.DELETE_SPECIFIC_CALENDAR(domain, calendarId)
      );
      return data;
    } catch (error) {
      console.error(`Failed to disconnect ${domain} calendar:`, error);
      throw new Error(`Failed to disconnect ${domain} Calendar`);
    }
  }
}

// Export singleton instance
export const calendarManagementService =
  CalendarManagementService.getInstance();
